import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { MainCard } from '../../ReportBuilder/Components/Beneficiary/BeneficiaryMainCard';
import {
  faCalendarCircleUser,
  faFileLines,
  faBolt,
} from '@fortawesome/pro-light-svg-icons';
import { Program } from '../../Reports/NewReportDialog';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { ReportsForProgram, ReportingType } from './api';
import { Selector } from 'shared/design-system/components/Selector';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactTooltip from 'shared/account/components/CustomTooltip/TrueImpactTooltip';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

export const tooltipAddNewReportYear =
  'Please select the first year of the program cycle you are reporting on. If your program period spans two years, such as an ' +
    'academic year, select the year the program begins in. (Example: A report spanning Sept., 2024 - August, 2025 will select 2024).';

export type ReportWithProgramYears = {
  [x: number]:
    | {
        [year: number]: ReportsForProgram | undefined;
      }
    | undefined;
};

export const mapReportPrograms = (
  data: ReportsForProgram[],
): { reportPrograms: ReportWithProgramYears; programs: Program[] } => {
  const programMap: { [id: number]: boolean } = {};
  const programs: Program[] = [];
  const result: ReportWithProgramYears = {};
  for (const report of data) {
    if (report.program && !programMap[report.programId]) {
      programMap[report.programId] = true;
      programs.push({
        Id: report.programId,
        name: report.program.name,
        managerId: report.program.managerId,
      });
    }
    if (!result[report.programId]) result[report.programId] = {};
    const year =
      typeof report.year === 'string' ? parseInt(report.year, 10) : report.year;
    result[report.programId]![year] = report;
  }
  return { reportPrograms: result, programs };
};

const programDisability = (
  reports: ReportWithProgramYears,
  tenYearsList: number[],
) => (p: Program): boolean => {
  const years = reports[p.Id];
  if (!years) return true;
  for (const y of tenYearsList) {
    if (reports[p.Id]![y]) return false;
  }
  return true;
};

const cloneExistingReportTooltip = (
  <>
    Clone Exiting report (s)- Create new reports by copying past reports-To
    create a new report — representing a new program/funding term — by copying
    (duplicating) an existing report
  </>
);
const createNewReportTooltip = (
  <>
    Create a brand new report-to create one or more new reports from scratch,
    provide the program/initiative name
  </>
);
const cloneReportFromExistingProgramTooltip = (
  <>
    Select Existing Report-From active reports (this year or beyond)- If a
    report already exists for the program/initiative and time period this
    investment is intended to fund
  </>
);

interface NewReportContentProps {
  setSelected: React.Dispatch<
    React.SetStateAction<{
      year: number | undefined;
      program: Program | undefined;
    }>
  >;
  setExisting: React.Dispatch<
    React.SetStateAction<{
      year: number | undefined;
      program: Program | undefined;
    }>
  >;
  showNext: boolean;
  reportsForProgram: RequestState<{
    reports: ReportsForProgram[];
  }>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  selected: {
    year: number | undefined;
    program: Program | undefined;
  };
  existing: {
    year: number | undefined;
    program: Program | undefined;
  };
  cloneType:
    | 'another-program-report'
    | 'new-report'
    | 'existing-report'
    | 'success'
    | undefined;
  setCloneType: React.Dispatch<
    React.SetStateAction<NewReportContentProps['cloneType']>
  >;
  reportingType: ReportingType;
  setReportingType: React.Dispatch<React.SetStateAction<ReportingType>>;
}

const currentYear = new Date().getFullYear();
const getYearsArray = (minYear: number, maxYear: number) => {
  const years: number[] = [];
  for (let index = minYear; index <= maxYear; index++) {
    years.push(index);
  }
  return years;
};
export const tenYearsList = getYearsArray(currentYear - 10, currentYear + 10);
const MAX_WIDTH = '460px';
const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  .clone-row {
    display: flex;
    flex-direction: row;
    max-width: ${MAX_WIDTH};
    margin-left: auto;
    margin-right: auto;
    gap: ${SPACERS.small};
    margin-bottom: ${SPACERS.medium};
    .clone-row-item {
      height: 100%;
    }
  }
  .or-separator {
    text-align: center;
    margin-bottom: ${SPACERS.medium};
  }
  .separator,
  .to-separator {
    text-align: center;
    margin-bottom: ${SPACERS.tiny};
  }
  .to-separator {
    margin-top: ${SPACERS.tiny};
  }
  .clone-another-program {
    max-width: ${MAX_WIDTH};
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.medium};
  }
  .dropdown-wrapper {
    text-align: center;
    margin-bottom: ${SPACERS.tiny};
    .dropdown-select {
      max-width: ${MAX_WIDTH};
    }
  }
  .reporting-type-row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
    max-width: ${MAX_WIDTH};
    margin: ${SPACERS.medium} auto 0 auto;
    gap: ${SPACERS.small};
    > * {
      flex: 1 1 0;
      display: flex;
    }
  }
  .reporting-type-label {
    text-align: center;
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.tiny};
  }
`;

const NewReportContent: React.FC<NewReportContentProps> = ({
  programsList,
  reportsForProgram,
  cloneType,
  showNext,
  selected,
  existing,
  setCloneType,
  setSelected,
  setExisting,
  reportingType,
  setReportingType,
}) => {
  const snapshotEnabled = useFeatureFlags('snapshot');
  const handleReportChange = useCallback(
    (year: number | undefined) => {
      setSelected(s => ({
        program: s.program,
        year,
      }));
    },
    [setSelected],
  );

  const handleExistingReportChange = useCallback(
    (year: number | undefined) => {
      setExisting(s => ({
        program: s.program,
        year,
      }));
    },
    [setExisting],
  );

  const handleProgramChange = useCallback(
    (program: Program | undefined) => {
      setSelected({
        program,
        year: undefined,
      });
    },
    [setSelected],
  );

  const handleExistingProgramChange = useCallback(
    (program: Program | undefined) => {
      setExisting({
        program,
        year: undefined,
      });
    },
    [setExisting],
  );

  const reports: ReportWithProgramYears = useMemo(() => {
    if (reportsForProgram.type !== 'REQUEST_SUCCESS') return {};
    const reportsData = reportsForProgram.data?.reports;
    if (!reportsData || !Array.isArray(reportsData)) return {};
    return mapReportPrograms(reportsData.filter(r => r.enabled)).reportPrograms;
  }, [reportsForProgram]);

  const programOptions = programsList.data?.allPrograms || [];

  if (
    reportsForProgram.type === 'REQUEST_INIT' ||
    reportsForProgram.type === 'REQUEST_START'
  ) {
    return (
      <TrueImpactLoading
        marginTop={0}
        marginBottom={0}
        paddingTop={30}
        paddingBottom={30}
      />
    );
  }

  if (reportsForProgram.type === 'REQUEST_ERROR') {
    return (
      <StyledDiv>
        <TrueImpactText color="critical" align="center">
          {reportsForProgram.error?.message ||
            'Unable to load existing reports. Please try again.'}
        </TrueImpactText>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      {showNext && cloneType === 'another-program-report' ? null : (
        <div className="clone-row">
          <MainCard
            icon={faCalendarCircleUser}
            onClick={() => setCloneType('existing-report')}
            selected={cloneType === 'existing-report'}
            text="Clone existing report"
            className="clone-row-item"
            tooltipComponent={cloneExistingReportTooltip}
          />
          <MainCard
            icon={faCalendarCircleUser}
            onClick={() => setCloneType('new-report')}
            selected={cloneType === 'new-report'}
            text="Create new report"
            className="clone-row-item"
            tooltipComponent={createNewReportTooltip}
          />
        </div>
      )}
      {/* {!showNext && <div className="or-separator">Or</div>}
      {showNext && cloneType !== 'another-program-report' ? null : (
        <Selector
          onClick={() => setCloneType('another-program-report')}
          selected={cloneType === 'another-program-report'}
          label="Clone a report from a existing program"
          variant="secondary"
          icon={faClone}
          animationScale={1.3}
          className="clone-another-program"
          tooltipComponent={cloneReportFromExistingProgramTooltip}
        />
      )} */}
      {showNext && cloneType !== 'new-report' && (
        <>
          <TrueImpactText variant="body-bold" className="separator">
            From
          </TrueImpactText>
          <div className="dropdown-wrapper">
            <TrueImpactDropdown
              value={existing.program}
              options={programOptions}
              getId={p => p.Id}
              getLabel={p => p.name}
              native={true}
              onChange={handleExistingProgramChange}
              onClear={undefined}
              getItemDisability={programDisability(reports, tenYearsList)}
              name="Please select existing program"
              className="dropdown-select"
            />
          </div>
          <div className="dropdown-wrapper">
            <TrueImpactDropdown
              value={existing.year}
              options={tenYearsList}
              disabled={!existing.program}
              getId={y => y}
              getLabel={y => y}
              getItemDisability={y =>
                !(existing.program && reports[existing.program.Id]?.[y])
              }
              native={true}
              onChange={handleExistingReportChange}
              onClear={undefined}
              name="Please select existing report year"
              className="dropdown-select"
            />
          </div>
        </>
      )}
      {showNext && (
        <>
          {cloneType !== 'new-report' && (
            <TrueImpactText
              variant="body-bold"
              className="separator to-separator"
            >
              To
            </TrueImpactText>
          )}
          <div className="dropdown-wrapper">
            <TrueImpactDropdown
              value={selected.program}
              options={programOptions}
              getId={p => p.Id}
              getLabel={p => p.name}
              native={true}
              onChange={handleProgramChange}
              onClear={undefined}
              getItemDisability={undefined}
              name="Please select your program"
              className="dropdown-select"
            />
          </div>
          <TrueImpactTooltip
            title={tooltipAddNewReportYear}
            containerDisplay="block"
            className="dropdown-wrapper"
          >
            <TrueImpactDropdown
              disabled={!selected.program}
              value={selected.year}
              options={tenYearsList}
              getId={y => y}
              getLabel={y => y}
              getItemDisability={y =>
                Boolean(selected.program && reports[selected.program.Id]?.[y])
              }
              native={true}
              onChange={handleReportChange}
              onClear={undefined}
              name="Please select your report year"
              className="dropdown-select"
            />
          </TrueImpactTooltip>
          {snapshotEnabled && (
            <>
              <TrueImpactText
                variant="body-bold"
                className="reporting-type-label"
              >
                Reporting Type
              </TrueImpactText>
              <div className="reporting-type-row">
                <Selector
                  variant="primary-radio"
                  icon={faFileLines}
                  label="Full Reporting"
                  selected={reportingType === 'FULL'}
                  onClick={() => setReportingType('FULL')}
                  tooltipComponent={
                    <>Complete report with all questions and indicators.</>
                  }
                />
                <Selector
                  variant="primary-radio"
                  icon={faBolt}
                  label="Impact Snapshot"
                  selected={reportingType === 'LIGHT'}
                  onClick={() => setReportingType('LIGHT')}
                  tooltipComponent={
                    <>
                      Lightweight report focused on the most essential impact
                      info.
                    </>
                  }
                />
              </div>
            </>
          )}
        </>
      )}
    </StyledDiv>
  );
};

export default NewReportContent;
